/**
 * Observable Badge Component
 *
 * Renders UI badges for observables attached to nodes.
 * Uses ui_hint metadata from SmartAssetPrimitives schemas.
 *
 * @see docs/discoveries/2026-01-05-ui-schema-consumption.md
 */
import { memo } from "react";
import type { Observable } from "../../../../types/valueFlows";

interface ObservableBadgeProps {
  /** Observable ID */
  id: string;
  /** Observable definition with ui_hint */
  observable: Observable;
  /** Badge size in pixels */
  size?: "sm" | "md" | "lg";
}

/**
 * Map badge_color from schema to Tailwind class
 */
const colorToClass: Record<string, string> = {
  blue: "bg-primary",
  green: "bg-green-500",
  yellow: "bg-yellow-500",
  orange: "bg-orange-500",
  red: "bg-red-500",
  purple: "bg-purple-500",
  cyan: "bg-cyan-500",
  amber: "bg-amber-500",
  // Fallback for hex colors handled separately
};

/**
 * Map glyph name to SVG icon
 * Supports heroicons-style names used in SmartAssetPrimitives schemas
 */
const GlyphIcon = ({ glyph, className }: { glyph: string; className?: string }) => {
  switch (glyph) {
    // Heroicons-style names from SmartAssetPrimitives schemas
    case "currency-dollar":
      return (
        <svg
          className={className}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M8 2v12M5 5.5c0-1.1.9-2 2-2h2c1.1 0 2 .9 2 2s-.9 2-2 2H6.5c-1.1 0-2 .9-2 2s.9 2 2 2H9c1.1 0 2-.9 2-2" />
        </svg>
      );
    case "chart-pie":
      return (
        <svg
          className={className}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <circle cx="8" cy="8" r="5.5" />
          <path d="M8 2.5v5.5h5.5" />
          <path d="M8 8L4 4" />
        </svg>
      );
    case "receipt-percent":
      return (
        <svg
          className={className}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M4 4l8 8M5.5 5a.5.5 0 100-1 .5.5 0 000 1zM10.5 12a.5.5 0 100-1 .5.5 0 000 1z" />
          <rect x="2" y="1" width="12" height="14" rx="1" />
        </svg>
      );
    case "lock-open":
      return (
        <svg
          className={className}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <rect x="3" y="7" width="10" height="7" rx="1" />
          <path d="M5 7V5a3 3 0 016 0" />
          <circle cx="8" cy="10.5" r="1" fill="currentColor" />
        </svg>
      );
    case "lock-closed":
      return (
        <svg
          className={className}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <rect x="3" y="7" width="10" height="7" rx="1" />
          <path d="M5 7V5a3 3 0 016 0v2" />
          <circle cx="8" cy="10.5" r="1" fill="currentColor" />
        </svg>
      );
    case "calendar":
      return (
        <svg
          className={className}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <rect x="2" y="3" width="12" height="11" rx="1" />
          <path d="M2 6h12M5 1v3M11 1v3" />
        </svg>
      );
    case "calendar-x":
    case "calendar-check":
      return (
        <svg
          className={className}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <rect x="2" y="3" width="12" height="11" rx="1" />
          <path d="M2 6h12M5 1v3M11 1v3" />
          {glyph === "calendar-check" ? (
            <path d="M5 10l2 2 4-4" />
          ) : (
            <path d="M6 9l4 4M10 9l-4 4" />
          )}
        </svg>
      );
    // Original glyphs for backwards compatibility
    case "stream":
      return (
        <svg className={className} viewBox="0 0 16 16" fill="currentColor">
          <path d="M3 3h2v10H3zM7 3h2v10H7zM11 3h2v10h-2z" opacity="0.3" />
          <path d="M4 5v6l2-3zM8 5v6l2-3zM12 5v6l2-3z" />
        </svg>
      );
    case "signal":
      return (
        <svg className={className} viewBox="0 0 16 16" fill="currentColor">
          <circle cx="8" cy="8" r="3" />
          <path
            d="M8 2v3M8 11v3M2 8h3M11 8h3"
            stroke="currentColor"
            strokeWidth="1.5"
            fill="none"
          />
        </svg>
      );
    case "gate":
      return (
        <svg className={className} viewBox="0 0 16 16" fill="currentColor">
          <rect x="4" y="4" width="8" height="8" rx="1" />
          <path d="M2 8h2M12 8h2" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      );
    case "clock":
      return (
        <svg className={className} viewBox="0 0 16 16" fill="currentColor">
          <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M8 4v4l3 2" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
      );
    case "value":
      return (
        <svg className={className} viewBox="0 0 16 16" fill="currentColor">
          <circle cx="8" cy="8" r="5" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M6 6h4l-2 4-2-4z" />
        </svg>
      );
    case "check":
      return (
        <svg className={className} viewBox="0 0 16 16" fill="currentColor">
          <path d="M3 8l3 3 7-7" stroke="currentColor" strokeWidth="2" fill="none" />
        </svg>
      );
    default:
      // Default dot indicator for unknown glyphs
      return (
        <svg className={className} viewBox="0 0 16 16" fill="currentColor">
          <circle cx="8" cy="8" r="4" />
        </svg>
      );
  }
};

/**
 * Size configurations
 */
const sizeConfig = {
  sm: { badge: "w-4 h-4", icon: "w-2.5 h-2.5" },
  md: { badge: "w-5 h-5", icon: "w-3 h-3" },
  lg: { badge: "w-6 h-6", icon: "w-4 h-4" },
};

/**
 * Position classes based on ui_hint.position
 */
const positionClasses: Record<string, string> = {
  "top-left": "-top-1 -left-1",
  "top-right": "-top-1 -right-1",
  "bottom-left": "-bottom-1 -left-1",
  "bottom-right": "-bottom-1 -right-1",
};

export const ObservableBadge = memo(({ id, observable, size = "sm" }: ObservableBadgeProps) => {
  const ui_hint = observable.ui_hint;
  const { badge, icon } = sizeConfig[size];

  // Determine background color
  let bgColorClass = "bg-sa-text-dim"; // Default
  if (ui_hint?.badge_color) {
    if (colorToClass[ui_hint.badge_color]) {
      bgColorClass = colorToClass[ui_hint.badge_color];
    } else if (ui_hint.badge_color.startsWith("#")) {
      // Handle hex color via inline style (see below)
      bgColorClass = "";
    }
  }

  // Determine glyph
  const glyph = ui_hint?.glyph || (observable.type === "boolean" ? "gate" : "signal");

  const bgStyle = ui_hint?.badge_color?.startsWith("#")
    ? { backgroundColor: ui_hint.badge_color }
    : undefined;

  return (
    <div
      className={`${badge} ${bgColorClass} rounded-full flex items-center justify-center text-white shadow-sm`}
      style={bgStyle}
      title={`${id}: ${observable.description || observable.type}`}
    >
      <GlyphIcon glyph={glyph} className={icon} />
    </div>
  );
});

ObservableBadge.displayName = "ObservableBadge";

/**
 * Container for multiple observable badges on a node
 */
interface ObservableBadgesContainerProps {
  badges: Array<{ id: string; observable: Observable }>;
  /** Position relative to node */
  position?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  size?: "sm" | "md" | "lg";
}

export const ObservableBadgesContainer = memo(
  ({ badges, position = "top-right", size = "sm" }: ObservableBadgesContainerProps) => {
    if (!badges || badges.length === 0) return null;

    const posClass = positionClasses[position] || positionClasses["top-right"];

    return (
      <div className={`absolute ${posClass} flex gap-0.5 z-10`}>
        {badges.map(({ id, observable }) => (
          <ObservableBadge key={id} id={id} observable={observable} size={size} />
        ))}
      </div>
    );
  },
);

ObservableBadgesContainer.displayName = "ObservableBadgesContainer";
